import { ApiError } from './ApiError';

// Señala que el refresh de token falló por una causa transitoria (red caída,
// timeout, backend 5xx) y NO porque el refresh token sea inválido/expirado/
// revocado. onRefreshToken debe relanzar este tipo en vez de devolver false
// para esos casos, así HttpClient no fuerza un logout por un problema de red.
export class TransientAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TransientAuthError';
  }
}

export interface HttpClientConfig {
  baseUrl: string;
  timeout?: number;
  headers?: Record<string, string>;
  getAuthToken?: () => string | null;
  getTenantId?: () => string | null;
  /** Sección activa del selector global del admin de sección. */
  getSectionId?: () => string | null;
  getClientType?: () => string | null;
  getCsrfToken?: () => string | null;
  onUnauthorized?: () => void;
  onForbidden?: (error: ApiError, method: string) => void;
  onRateLimited?: (retryAfter?: number) => void;
  onRefreshToken?: () => Promise<boolean>; // Returns true if refresh was successful
}

export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
  timeout?: number;
  body?: unknown; // Para DELETE con body
  responseType?: 'json' | 'blob' | 'text'; // Tipo de respuesta esperada
  signal?: AbortSignal;
}

export interface HttpClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, options?: RequestOptions): Promise<T>;
}

export const createHttpClient = (config: HttpClientConfig): HttpClient => {
  const { baseUrl, timeout = 30000, headers: defaultHeaders = {}, getAuthToken, getTenantId, getSectionId, getClientType, getCsrfToken, onUnauthorized, onForbidden, onRateLimited, onRefreshToken } = config;

  // Mutex para evitar múltiples refresh simultáneos
  let refreshPromise: Promise<boolean> | null = null;

  const buildUrl = (path: string, params?: Record<string, string | number | boolean | undefined>): string => {
    // Sin '/' final, new URL('v1/x', 'http://h/api') descarta '/api' (→ http://h/v1/x, 404).
    // .env.example de web no lo trae; refreshSession ya arma `${base}/v1/...` y no lo perdía.
    const url = new URL(path, baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  };

  const buildHeaders = (customHeaders?: Record<string, string>, skipContentType?: boolean): Headers => {
    const headers = new Headers({
      // No setear Content-Type para FormData - el browser lo hace automáticamente con boundary
      ...(!skipContentType && { 'Content-Type': 'application/json' }),
      ...defaultHeaders,
      ...customHeaders,
    });

    const token = getAuthToken?.();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const tenantId = getTenantId?.();
    if (tenantId && !headers.has('x-tenant-id')) {
      headers.set('x-tenant-id', tenantId);
    }

    const sectionId = getSectionId?.();
    if (sectionId && !headers.has('x-section-id')) {
      headers.set('x-section-id', sectionId);
    }

    // Le indica al backend que el cliente es un browser (habilita el flujo de
    // refresh token en cookie HttpOnly en vez de body) y adjunta el token
    // CSRF requerido por /auth/refresh y /auth/logout bajo ese esquema.
    const clientType = getClientType?.();
    if (clientType && !headers.has('x-client-type')) {
      headers.set('x-client-type', clientType);
    }

    const csrfToken = getCsrfToken?.();
    if (csrfToken && !headers.has('x-csrf-token')) {
      headers.set('x-csrf-token', csrfToken);
    }

    return headers;
  };

  const handleResponse = async <T>(response: Response, method: string, responseType?: 'json' | 'blob' | 'text', skipUnauthorizedCallback?: boolean): Promise<T> => {
    if (!response.ok) {
      let errorBody: unknown;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        errorBody = await response.json();
      } else {
        errorBody = await response.text();
      }

      const apiError = ApiError.fromResponse(response, errorBody);

      // Solo llamar onUnauthorized si no estamos en modo retry
      if (response.status === 401 && !skipUnauthorizedCallback) {
        onUnauthorized?.();
      }

      // 403: el token es válido pero el permiso fue denegado — puede ser que el rol
      // del usuario haya cambiado a mitad de sesión. Se notifica para que la capa de
      // auth pueda resincronizar las habilidades sin forzar un logout. Se pasa el
      // método HTTP para que esa capa decida si vale la pena interrumpir al usuario
      // (una mutación que él disparó) o resincronizar en silencio (una carga de fondo).
      if (response.status === 403) {
        onForbidden?.(apiError, method);
      }

      if (response.status === 429) {
        const retryAfterHeader = response.headers.get('Retry-After');
        const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 60;
        onRateLimited?.(retryAfter);
      }

      throw apiError;
    }

    // Manejar diferentes tipos de respuesta
    if (responseType === 'blob') {
      return (await response.blob()) as T;
    }

    if (responseType === 'text') {
      return (await response.text()) as T;
    }

    // 204 No Content (típico en DELETE) no tiene body que parsear
    if (response.status === 204) {
      return undefined as T;
    }

    // Por defecto, intentar JSON. El body puede venir vacío aunque el header
    // Content-Type sea application/json (p. ej. un DELETE con status 200 sin
    // payload) — parsear un string vacío con response.json() lanza
    // "Unexpected end of JSON input" y hace ver como fallido un request que
    // en realidad tuvo éxito en el backend.
    const contentType = response.headers.get('content-type');
    const rawText = await response.text();
    if (!rawText) {
      return undefined as T;
    }

    if (contentType?.includes('application/json')) {
      const json = JSON.parse(rawText);
      // Desenvolver respuestas envueltas en { success, data } EXCEPTO respuestas paginadas
      if (json && typeof json === 'object' && 'success' in json && 'data' in json && !('meta' in json)) {
        return json.data as T;
      }
      return json as T;
    }

    // Si no es JSON, retornar como texto
    return rawText as T;
  };

  // Función interna que ejecuta la petición real
  const executeRequest = async <T>(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestOptions,
    skipUnauthorizedCallback?: boolean
  ): Promise<T> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options?.timeout ?? timeout);

    const externalSignal = options?.signal;
    if (externalSignal?.aborted) {
      controller.abort(externalSignal.reason);
    } else {
      externalSignal?.addEventListener('abort', () => controller.abort(externalSignal.reason), { once: true });
    }

    try {
      // Para DELETE, usar body de options si está presente, sino usar el body del parámetro
      const requestBody = options?.body !== undefined ? options.body : body;

      // Detectar si es FormData para manejar headers y body correctamente
      const isFormData = requestBody instanceof FormData;
      const headers = buildHeaders(options?.headers, isFormData);

      // Para DELETE con body, asegurar que Content-Type esté presente
      if (method === 'DELETE' && requestBody && !isFormData) {
        // DELETE con body - asegurar Content-Type
        if (!headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json');
        }
      } else if (!requestBody && method !== 'GET') {
        // Sin body (excepto GET), remover Content-Type para evitar problemas con algunos servidores
        headers.delete('Content-Type');
      }

      // Para FormData, pasar directamente sin stringify
      // Para otros tipos, hacer JSON.stringify
      const finalBody = isFormData
        ? requestBody as FormData
        : (requestBody ? JSON.stringify(requestBody) : undefined);

      const response = await fetch(buildUrl(path, options?.params), {
        method,
        headers,
        body: finalBody,
        signal: controller.signal,
        // Necesario para que el browser adjunte la cookie HttpOnly del refresh
        // token en las rutas de /auth. Inocuo en el resto de las rutas: esa
        // cookie tiene Path=/api/v1/auth, así que no viaja fuera de ese scope.
        credentials: 'include',
      });

      return await handleResponse<T>(response, method, options?.responseType, skipUnauthorizedCallback);
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Función principal con retry automático para 401
  const request = async <T>(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> => {
    try {
      // Primera intento - si falla con 401, intentaremos refresh
      return await executeRequest<T>(method, path, body, options, true);
    } catch (error) {
      // Solo intentar refresh si es un error 401 y tenemos onRefreshToken
      if (error instanceof ApiError && error.statusCode === 401 && onRefreshToken) {
        let refreshed = false;
        try {
          // Usar mutex para evitar múltiples refresh simultáneos
          if (!refreshPromise) {
            refreshPromise = onRefreshToken().finally(() => {
              refreshPromise = null;
            });
          }

          refreshed = await refreshPromise;
        } catch (refreshError) {
          // Fallo transitorio (red/timeout/5xx del refresh): el refresh token
          // sigue siendo válido, solo no se pudo confirmar ahora mismo. No hay
          // que cerrar sesión - se propaga el 401 original y la próxima
          // request reintentará el refresh.
          if (refreshError instanceof TransientAuthError) {
            throw error;
          }
          // Cualquier otro error de refresh: tratar como fallo real (mismo
          // comportamiento que si onRefreshToken hubiera devuelto false).
        }

        if (refreshed) {
          // Retry con el nuevo token, fuera del try: un 403/404/500 del retry
          // es un error de la request, no del refresh, y no debe cerrar sesión
          // (antes lo hacía: un residente perdía la sesión al recargar porque
          // /sections salía sin token, 401 -> refresh ok -> retry 403 -> logout).
          // Si el retry vuelve a dar 401, handleResponse llama onUnauthorized.
          return await executeRequest<T>(method, path, body, options, false);
        }

        // Refresh falló o no fue exitoso, llamar onUnauthorized y propagar error
        onUnauthorized?.();
      }

      throw error;
    }
  };

  return {
    get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, body, options),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PUT', path, body, options),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PATCH', path, body, options),
    delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, undefined, { ...options, body: options?.body }),
  };
};
